import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { always, compose, ifElse, prop } from 'ramda';
import { withHandlers } from 'recompose';

import AuthForm from 'app/features/authentication/components/AuthForm';
import AuthLayout from 'app/features/authentication/components/AuthLayout.styled';
import LoginForgotPassword from 'app/features/authentication/components/Login/LoginForgotPassword';
import CallToAction from 'app/components/layout/CallToAction.styled';
import BasicLink from 'app/components/Links/BasicLink.styled';
import LocalizedLink from 'app/features/localization/components/LocalizedLink/LocalizedLink';
import Text from 'app/components/layout/Text.styled';
import { HOME_PATH } from 'app/router/constants/paths';
import { MIDDLE } from 'app/analytics/constants/positions';
import { loginRequest } from 'app/features/authentication/actions/login';
import { registrationSelected } from 'app/features/authentication/actions/register';
import connect from 'app/libs/connect';
import withCriteoTagTracker from 'app/analytics/enhancers/withCriteoAnalytics';
import { PRODUCT_TAG } from 'app/analytics/constants/criteo';
import FloatingLayout from 'app/components/layout/FloatingLayout';
import { isUserAuthenticated } from 'app/features/user/selectors';
import withDispatchOnMount from 'app/enhancers/withDispatchOnMount';
import { localRedirect } from 'app/router/actions';
import noop from 'app/libs/noop';
import { LOGIN_FORM } from 'app/features/authentication/constants/formTypes';
import { registerLink } from 'app/features/authentication/selectors';

const UI = ({ onLogin, onClick, registerLink }) => (
  <FloatingLayout>
    <FloatingLayout.ContentBlock>
      <AuthLayout>
        <AuthForm
          headingTitleKey="authentication_login_title"
          buttonTextKey="authentication_login_title"
          emailErrorKey="authentication_email_error"
          emailFloatingTextKey="authentication_email_floating_text"
          validateEmail={always(true)}
          passwordErrorKey="authentication_password_error"
          passwordFloatingTextKey="authentication_password_floating_text"
          passwordWithSpaceTextKey="authentication_password_contains_space"
          formHandler={onLogin}
          formType={LOGIN_FORM}
        />
        <LoginForgotPassword />
        <Text size="xl">
          <FormattedMessage
            id="authentication_no_account"
            values={{
              noAccountCta: (
                <BasicLink
                  testId="login-create-account-cta"
                  as={LocalizedLink}
                  to={registerLink}
                  capitalize
                  onClick={onClick}
                >
                  <CallToAction>
                    <FormattedMessage id="authentication_no_account_cta" />
                  </CallToAction>
                </BasicLink>
              ),
            }}
          />
        </Text>
      </AuthLayout>
    </FloatingLayout.ContentBlock>
  </FloatingLayout>
);

UI.propTypes = {
  onClick: PropTypes.func.isRequired,
  onLogin: PropTypes.func.isRequired,
  registerLink: PropTypes.string.isRequired,
};

UI.displayName = 'Login';

const onClick =
  ({ registrationSelected }) =>
  () => {
    registrationSelected({ position: MIDDLE });
  };

const Login = compose(
  connect(
    { isUserAuthenticated, registerLink },
    { loginRequest, registrationSelected },
  ),
  withDispatchOnMount(
    ifElse(
      prop('isUserAuthenticated'),
      always(localRedirect(HOME_PATH)),
      always(noop),
    ),
  ),
  withCriteoTagTracker(PRODUCT_TAG),
  withHandlers({ onLogin: prop('loginRequest'), onClick }),
)(UI);

export { UI, onClick };

export default Login;
